import React, { useEffect, useRef, useState } from "react";
import { CurrentUser } from "../../../../../stores/CurrentUserStore/Types";
import { useAppDispatch, AppState } from "../../../../../stores/CommunityStore";
import { useSelector } from "react-redux";
import { createPost } from "../../../../../stores/CommunityStore/posts/actions/createPost";
import { RESET_POST_CREATED } from "../../../../../stores/CommunityStore/posts/types";
import AttachmentCard from "./AttachmentCard";
import { LinkIcon, PhotoIcon } from "./InlineIcons";
import { classifyUrl } from "./classifyUrl";
import { fetchLinkPreview, toResolvedLinkAttachment } from "./eagerLinkFetch";
import Editor from "./Editor";
import Toolbar from "./Editor/Toolbar";
import { ActiveFormats, EditorHandle, noActiveFormats } from "./Editor/types";
import {
  ComposerDraft,
  emptyDraft,
  hasNothingToPost,
  ImageAttachment,
  LinkAttachment,
  toImageAttachment,
} from "./types";

type ComposerMode = "create" | "edit";

interface ComposerProps {
  currentUser?: CurrentUser;
  selectedTopicUuid?: string;
  mode?: ComposerMode;
  initialContent?: string;
  initialLinks?: LinkAttachment[];
  existingImages?: ImageAttachment[];
  onSave?: (
    markdown: string,
    linkUrls: string[],
    removeImages: boolean,
    image?: File,
  ) => Promise<void> | void;
  onCancel?: () => void;
}

const Composer = ({
  currentUser,
  selectedTopicUuid,
  mode = "create",
  initialContent,
  initialLinks,
  existingImages,
  onSave,
  onCancel,
}: ComposerProps) => {
  const isEdit = mode === "edit";

  const dispatch = useAppDispatch();
  const { created, creatingPost } = useSelector(
    (state: AppState) => state.posts,
  );

  // Attachments for the post. The editor's text is held in the contenteditable
  // itself and read on submit; we only mirror its empty/non-empty state here.
  const [draft, setDraft] = useState<ComposerDraft>(() => ({
    photos: [],
    links: initialLinks ?? [],
  }));
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isLinkBarOpen, setIsLinkBarOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>(noActiveFormats);
  const [imagesRemoved, setImagesRemoved] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const editorRef = useRef<EditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const linkFieldRef = useRef<HTMLInputElement>(null);
  const idRef = useRef(0);

  // Per in-flight eager fetch, keyed by attachment id, so a removal can
  // abort the request and cancel its bare-timeout timer.
  const linkFetchControllers = useRef(new Map<string, AbortController>());
  const linkBareTimers = useRef(new Map<string, number>());

  const makeId = () => `att-${(idRef.current += 1)}`;

  const cancelLinkFetch = (id: string) => {
    linkFetchControllers.current.get(id)?.abort();
    linkFetchControllers.current.delete(id);

    const timer = linkBareTimers.current.get(id);
    if (timer !== undefined) window.clearTimeout(timer);
    linkBareTimers.current.delete(id);
  };

  // Applies a fetch/timeout outcome to the attachment with this id, if it's
  // still around (the user may have removed it while the request was in
  // flight). `onlyIfLoading` is set by the 5s timeout so a response that
  // lands in the last moment before it fires isn't clobbered back to bare.
  const applyLinkResult = (
    id: string,
    metadata: Parameters<typeof toResolvedLinkAttachment>[1],
    onlyIfLoading = false,
  ) => {
    setDraft((current) => {
      const index = current.links.findIndex((item) => item.id === id);
      if (index === -1) return current;

      const existing = current.links[index];
      if (onlyIfLoading && existing.status !== "loading") return current;

      const links = [...current.links];
      links[index] = toResolvedLinkAttachment(existing, metadata);
      return { ...current, links };
    });
  };

  const beginEagerFetch = (id: string, url: string) => {
    const controller = new AbortController();
    linkFetchControllers.current.set(id, controller);

    const bareTimer = window.setTimeout(() => {
      applyLinkResult(id, null, true);
    }, 5000);
    linkBareTimers.current.set(id, bareTimer);

    fetchLinkPreview(url, controller.signal).then((metadata) => {
      const timer = linkBareTimers.current.get(id);
      if (timer !== undefined) window.clearTimeout(timer);
      linkBareTimers.current.delete(id);
      linkFetchControllers.current.delete(id);

      applyLinkResult(id, metadata);
    });
  };

  // Reset the whole draft once the store confirms the post was created.
  useEffect(() => {
    if (isEdit || !created) return;

    draft.photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    draft.links.forEach((link) => cancelLinkFetch(link.id));
    setDraft(emptyDraft());
    setIsEditorEmpty(true);
    setIsLinkBarOpen(false);
    setLinkValue("");
    editorRef.current?.clear();
    dispatch({ type: RESET_POST_CREATED });
  }, [created, dispatch, draft.photos, draft.links, isEdit]);

  // Release object URLs and abort in-flight fetches when the component
  // unmounts.
  useEffect(
    () => () => {
      draft.photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      draft.links.forEach((link) => cancelLinkFetch(link.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addLinkFromUrl = (raw: string): boolean => {
    const classified = classifyUrl(raw);
    if (!classified) return false;

    const id = makeId();
    const link: LinkAttachment = { id, ...classified, status: "loading" };
    setDraft((current) => ({ ...current, links: [...current.links, link] }));
    beginEagerFetch(id, classified.url);

    return true;
  };

  /* ---- photo ---- */
  const onPhotoButtonClick = () => fileInputRef.current?.click();

  const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(event.target.files || []).find((item) =>
      item.type.startsWith("image/"),
    );

    if (file) {
      setDraft((current) =>
        current.photos.length
          ? current
          : {
              ...current,
              photos: [
                {
                  id: makeId(),
                  file,
                  previewUrl: URL.createObjectURL(file),
                  alt: file.name,
                },
              ],
            },
      );
    }

    // Allow re-selecting the same file.
    event.target.value = "";
  };

  const removePhoto = (id: string) => {
    setDraft((current) => {
      const photo = current.photos.find((item) => item.id === id);
      if (photo) URL.revokeObjectURL(photo.previewUrl);

      return {
        ...current,
        photos: current.photos.filter((item) => item.id !== id),
      };
    });
  };

  /* ---- link bar ---- */
  const toggleLinkBar = () => {
    setIsLinkBarOpen((open) => {
      const next = !open;
      if (next) window.setTimeout(() => linkFieldRef.current?.focus(), 0);
      return next;
    });
  };

  const closeLinkBar = () => {
    setIsLinkBarOpen(false);
    setLinkValue("");
  };

  const submitLink = () => {
    const value = linkValue.trim();
    if (!value) return;

    if (addLinkFromUrl(value)) {
      closeLinkBar();
    } else {
      linkFieldRef.current?.focus();
    }
  };

  const removeLink = (id: string) => {
    cancelLinkFetch(id);
    setDraft((current) => ({
      ...current,
      links: current.links.filter((item) => item.id !== id),
    }));
  };

  const keptImages = isEdit && !imagesRemoved ? existingImages ?? [] : [];

  /* ---- submit ---- */
  const submitting = isEdit ? isSavingEdit : creatingPost;
  const nothingToSubmit = hasNothingToPost(
    draft,
    isEditorEmpty,
    isEdit && keptImages.length > 0,
  );

  const submitPost = async () => {
    if (nothingToSubmit || submitting) return;

    const markdown = editorRef.current?.getMarkdown().trim() || "";
    const linkUrls = draft.links.map((link) => link.url);

    if (isEdit) {
      setIsSavingEdit(true);
      try {
        await onSave?.(markdown, linkUrls, imagesRemoved, draft.photos[0]?.file);
      } finally {
        setIsSavingEdit(false);
      }
      return;
    }

    dispatch(
      createPost(markdown, selectedTopicUuid, draft.photos[0]?.file, linkUrls),
    );
  };

  const handleCancel = () => {
    draft.links.forEach((link) => cancelLinkFetch(link.id));
    onCancel?.();
  };

  return (
    <div className="community-composer">
      {!isEdit && currentUser && (
        <img
          className="community-composer__avatar"
          src={currentUser.profile_picture}
          alt="Your profile"
        />
      )}

      <div className="community-composer__body">
        <Editor
          ref={editorRef}
          onEmptyChange={setIsEditorEmpty}
          onUrlPasted={addLinkFromUrl}
          onActiveFormatsChange={setActiveFormats}
          placeholder="Share your wisdom or thoughts here..."
          ariaLabel={isEdit ? "Edit post" : "Write a post"}
          initialHTML={isEdit ? initialContent : undefined}
        />

        {isLinkBarOpen && (
          <div className="community-composer__linkbar">
            <input
              ref={linkFieldRef}
              type="url"
              inputMode="url"
              className="community-composer__linkfield"
              value={linkValue}
              onChange={(event) => setLinkValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitLink();
                }
                if (event.key === "Escape") closeLinkBar();
              }}
              placeholder="Paste a YouTube, Vimeo or website link..."
              aria-label="Add a link"
            />
            <button
              className="community-composer__linkadd"
              type="button"
              onClick={submitLink}
            >
              Add
            </button>
            <button
              className="community-composer__linkcancel"
              type="button"
              onClick={closeLinkBar}
            >
              Cancel
            </button>
          </div>
        )}

        {(keptImages.length > 0 ||
          draft.photos.length > 0 ||
          draft.links.length > 0) && (
          <div className="community-composer__previews" aria-live="polite">
            {keptImages.map((image) => (
              <AttachmentCard
                key={image.id}
                image={image}
                onRemove={() => setImagesRemoved(true)}
              />
            ))}
            {draft.photos.map((photo) => (
              <AttachmentCard
                key={photo.id}
                image={toImageAttachment(photo)}
                onRemove={() => removePhoto(photo.id)}
              />
            ))}
            {draft.links.map((link) => (
              <AttachmentCard
                key={link.id}
                link={link}
                onRemove={() => removeLink(link.id)}
              />
            ))}
          </div>
        )}

        <div className="community-composer__toolbar">
          <div className="community-composer__tools">
            <Toolbar
              activeFormats={activeFormats}
              onFormat={(command) => editorRef.current?.execFormat(command)}
            />
            <button
              className="community-composer__tool"
              type="button"
              aria-label="Add a photo"
              disabled={draft.photos.length > 0 || keptImages.length > 0}
              onClick={onPhotoButtonClick}
            >
              <PhotoIcon />
              <span>Photo</span>
            </button>
            <button
              className={`community-composer__tool ${
                isLinkBarOpen ? "community-composer__tool--active" : ""
              }`}
              type="button"
              aria-label="Add a link"
              aria-expanded={isLinkBarOpen}
              onClick={toggleLinkBar}
            >
              <LinkIcon />
              <span>Link</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="community-composer__file"
              accept="image/*"
              tabIndex={-1}
              aria-hidden="true"
              onChange={onFilesSelected}
            />
          </div>

          <div className="community-composer__actions">
            {isEdit && (
              <button
                className="post-edit__cancel"
                type="button"
                onClick={handleCancel}
              >
                Cancel
              </button>
            )}

            <button
              className="community-composer__button"
              type="button"
              disabled={nothingToSubmit || submitting}
              onClick={submitPost}
            >
              {submitting && <span className="reply-submit-spinner" />}
              {isEdit
                ? submitting
                  ? "Saving..."
                  : "Save"
                : submitting
                  ? "Posting..."
                  : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Composer;
